import { saveAs } from 'file-saver';
import { formatDate, formatDateTime } from './dateUtils';

/**
 * Resolve a cell's value from a row using the column definition.
 */
const resolveCellValue = (item, column) => {
  let value = item[column.key];

  // Use custom exportValue function if provided
  if (column.exportValue && typeof column.exportValue === 'function') {
    value = column.exportValue(item);
  }
  // Use custom render function if provided and no exportValue
  else if (column.render && typeof column.render === 'function' && !column.exportValue) {
    const rendered = column.render(item);
    // Extract text content from JSX/HTML if needed
    if (typeof rendered === 'object' && rendered?.props?.children) {
      value = rendered.props.children;
    } else if (typeof rendered === 'string') {
      value = rendered;
    }
  }

  return formatValueForExport(value, column.key);
};

/**
 * Write one worksheet: styled header row, data rows, auto-sized columns.
 */
const addWorksheet = (workbook, sheetName, columns, data) => {
  const worksheet = workbook.addWorksheet(sheetName);

  // Add header row with styling
  const headerRow = worksheet.addRow(columns.map(col => col.label));
  headerRow.font = { bold: true };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' }
  };

  // Add data rows
  data.forEach(item => {
    const row = worksheet.addRow(columns.map(column => resolveCellValue(item, column)));

    // Multi-line cells (e.g. the tasks summary) need wrapping to be readable.
    row.eachCell({ includeEmpty: false }, (cell) => {
      if (typeof cell.value === 'string' && cell.value.includes('\n')) {
        cell.alignment = { wrapText: true, vertical: 'top' };
      }
    });
  });

  // Auto-size columns based on content
  worksheet.columns.forEach((column, index) => {
    let maxLength = 0;
    const columnIndex = index + 1;

    worksheet.getColumn(columnIndex).eachCell({ includeEmpty: true }, (cell) => {
      const cellText = cell.value ? cell.value.toString() : '';
      // Width should follow the longest line, not the whole wrapped blob.
      cellText.split('\n').forEach((line) => {
        if (line.length > maxLength) {
          maxLength = line.length;
        }
      });
    });

    // Set column width with min/max constraints
    worksheet.getColumn(columnIndex).width = Math.min(Math.max(maxLength + 2, 10), 50);
  });

  return worksheet;
};

/**
 * Export data to Excel format (with lazy-loaded ExcelJS library)
 * @param {Array} data - Array of data objects to export
 * @param {Array} columns - Array of column definitions with label and key properties
 * @param {string} filename - Name of the file to be downloaded
 * @param {Object} [options]
 * @param {string} [options.sheetName] - Name of the primary worksheet (default 'Data')
 * @param {Array} [options.extraSheets] - Additional worksheets: [{ name, columns, data }]
 */
export const exportToExcel = async (data, columns, filename, options = {}) => {
  try {
    // Lazy load ExcelJS only when needed (saves ~500KB from initial bundle)
    const ExcelJS = (await import('exceljs')).default;

    const { sheetName = 'Data', extraSheets = [] } = options;

    // Create a new workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    addWorksheet(workbook, sheetName, columns, data);

    // Additional sheets (e.g. a Tasks tab alongside the exported items)
    extraSheets.forEach((sheet) => {
      if (!sheet || !Array.isArray(sheet.columns) || sheet.columns.length === 0) return;
      addWorksheet(workbook, sheet.name || 'Sheet', sheet.columns, sheet.data || []);
    });

    // Generate buffer and save file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    saveAs(blob, filename);
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw new Error('Failed to export to Excel');
  }
};

/**
 * Export data to CSV format
 * @param {Array} data - Array of data objects to export
 * @param {Array} columns - Array of column definitions with label and key properties
 * @param {string} filename - Name of the file to be downloaded
 */
export const exportToCSV = (data, columns, filename) => {
  try {
    // Create CSV header row
    const headers = columns.map(col => escapeCSVValue(col.label));
    let csvContent = headers.join(',') + '\n';

    // Add data rows
    data.forEach(item => {
      const row = columns.map(column => escapeCSVValue(resolveCellValue(item, column)));

      csvContent += row.join(',') + '\n';
    });

    // Create blob and save file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, filename);
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    throw new Error('Failed to export to CSV');
  }
};

/**
 * Format individual values for export
 * @param {any} value - The value to format
 * @param {string} columnKey - The column key to determine formatting
 * @returns {string} Formatted value
 */
export const formatValueForExport = (value, columnKey) => {
  // Handle null/undefined values
  if (value === null || value === undefined) {
    return '-';
  }

  // Detect date fields and format them
  if (columnKey && (
    columnKey.endsWith('_on') || 
    columnKey.endsWith('_date') || 
    columnKey.endsWith('_at') ||
    columnKey.includes('date') ||
    columnKey.includes('created') ||
    columnKey.includes('updated') ||
    columnKey.includes('due') ||
    columnKey.includes('completed')
  )) {
    try {
      if (value instanceof Date || typeof value === 'string') {
        // Use formatDateTime for timestamp fields, formatDate for date-only fields
        if (columnKey.endsWith('_at') || columnKey.includes('time')) {
          return formatDateTime(value);
        } else {
          return formatDate(value);
        }
      }
    } catch (error) {
      console.warn('Error formatting date:', error);
      return String(value);
    }
  }

  // Handle boolean values
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  // Handle objects/arrays
  if (typeof value === 'object' && value !== null) {
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : '-';
    }
    return JSON.stringify(value);
  }

  // Handle numbers (ensure no scientific notation for IDs)
  if (typeof value === 'number') {
    return value.toString();
  }

  // Return string values as-is
  return String(value);
};

/**
 * Escape CSV values to handle commas, quotes, and newlines
 * @param {string} value - The value to escape
 * @returns {string} Escaped CSV value
 */
const escapeCSVValue = (value) => {
  if (typeof value !== 'string') {
    value = String(value);
  }
  
  // If value contains comma, quote, or newline, wrap in double quotes
  if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
    // Escape existing double quotes by doubling them
    value = value.replace(/"/g, '""');
    return `"${value}"`;
  }
  
  return value;
};

/**
 * Generate consistent filenames for exports
 * @param {string} baseName - Base name (e.g., 'items', 'tasks')
 * @param {string} viewName - Current view name or 'default'
 * @param {string} format - File format ('xlsx' or 'csv')
 * @returns {string} Generated filename
 */
export const generateFilename = (baseName, viewName, format) => {
  // Get current date in YYYY-MM-DD format
  const date = new Date();
  const dateString = date.toISOString().split('T')[0];
  
  // Sanitize view name to remove special characters
  const sanitizedViewName = (viewName || 'default')
    .replace(/[^a-zA-Z0-9-_]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  
  // Handle empty view name
  const finalViewName = sanitizedViewName || 'default';
  
  return `${baseName}-${finalViewName}-${dateString}.${format}`;
};